import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useLiveScores } from "../../hooks";
import { useTranslation } from "../../hooks/useTranslation";
import { hub } from "./hubSurface";

type MatchRow = {
  id: number;
  time: string;
  statusTitle: string;
  isLive: boolean;
  liveTime: string;
  hostName: string;
  hostLogo: string;
  guestName: string;
  guestLogo: string;
  hostGoals?: number;
  guestGoals?: number;
  leagueTitle: string;
};

const isIranLeague = (title: string) =>
  /ایران|iran|لیگ برتر\s*ایران|persian gulf pro league/i.test(title);

const HubLiveScores: React.FC = () => {
  const { t } = useTranslation();
  const { data: leagues = [], isLoading, isError } = useLiveScores();

  const rows = useMemo(() => {
    const collected: MatchRow[] = [];
    const sorted = [...leagues].sort((a, b) => {
      const aIran = isIranLeague(a.title) ? 0 : 1;
      const bIran = isIranLeague(b.title) ? 0 : 1;
      return aIran - bIran;
    });

    for (const league of sorted) {
      const today = league.dates?.[0];
      if (!today?.matches?.length) continue;
      for (const match of today.matches) {
        collected.push({
          id: match.id,
          time: match.time,
          statusTitle: match.statusTitle,
          isLive: match.isLive,
          liveTime: match.liveTime,
          hostName: match.host.name,
          hostLogo: match.host.logo,
          guestName: match.guest.name,
          guestLogo: match.guest.logo,
          hostGoals: match.goals?.host,
          guestGoals: match.goals?.guest,
          leagueTitle: league.title,
        });
        if (collected.length >= 8) return collected;
      }
    }
    return collected;
  }, [leagues]);

  return (
    <section
      aria-labelledby="hub-livescores-heading"
      className={`overflow-hidden rounded-xl border ${hub.border} bg-white`}
    >
      <div className={`flex items-center justify-between gap-3 border-b ${hub.border} px-4 py-3`}>
        <div className="flex min-w-0 items-center gap-2">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#b42318] opacity-40" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#b42318]" />
          </span>
          <h2
            id="hub-livescores-heading"
            className={`truncate text-base font-semibold tracking-tight ${hub.ink}`}
          >
            {t("hub.liveScoresTitle")}
          </h2>
        </div>
        <Link to="/livescores" className={`shrink-0 text-xs font-medium ${hub.accent} hover:opacity-80`}>
          {t("hub.seeAll")}
        </Link>
      </div>

      <div className="divide-y divide-[#eeebe6]">
        {isLoading &&
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse px-4 py-3">
              <div className="mb-2 h-3 w-24 rounded bg-[#f0eeea]" />
              <div className="h-8 rounded bg-[#f6f5f2]" />
            </div>
          ))}

        {isError && (
          <p className={`px-4 py-6 text-center text-sm ${hub.mute}`}>{t("hub.liveScoresError")}</p>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <p className={`px-4 py-6 text-center text-sm ${hub.mute}`}>{t("hub.liveScoresEmpty")}</p>
        )}

        {rows.map((m) => (
          <div key={m.id} className="px-4 py-3 transition-colors hover:bg-[#fbfbfa]">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className={`text-[11px] leading-snug ${hub.faint}`}>{m.leagueTitle}</span>
              {m.isLive ? (
                <span className="shrink-0 text-[11px] font-semibold tabular-nums text-[#b42318]">
                  {m.liveTime || t("hub.live")}
                </span>
              ) : (
                <span className={`shrink-0 text-[11px] tabular-nums ${hub.faint}`}>
                  {m.statusTitle || m.time}
                </span>
              )}
            </div>
            <div className="flex items-stretch gap-3">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-start gap-2">
                  {m.hostLogo ? (
                    <img src={m.hostLogo} alt="" className="mt-0.5 h-5 w-5 shrink-0 object-contain" />
                  ) : (
                    <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#f0eeea]" />
                  )}
                  <span className={`text-[13px] font-semibold leading-snug ${hub.ink}`}>{m.hostName}</span>
                </div>
                <div className="flex items-start gap-2">
                  {m.guestLogo ? (
                    <img src={m.guestLogo} alt="" className="mt-0.5 h-5 w-5 shrink-0 object-contain" />
                  ) : (
                    <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#f0eeea]" />
                  )}
                  <span className={`text-[13px] font-semibold leading-snug ${hub.ink}`}>{m.guestName}</span>
                </div>
              </div>
              <div className="flex w-12 shrink-0 flex-col items-center justify-center border-s border-[#eeebe6] ps-2">
                {m.hostGoals != null && m.guestGoals != null ? (
                  <>
                    <span className={`text-sm font-semibold tabular-nums ${hub.ink}`}>{m.hostGoals}</span>
                    <span className={`text-sm font-semibold tabular-nums ${hub.ink}`}>{m.guestGoals}</span>
                  </>
                ) : (
                  <span className={`text-xs tabular-nums ${hub.faint}`}>{m.time}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default HubLiveScores;
